import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DataTable from "@/components/DataTable";
import { Plus, GraduationCap, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CAN_MANAGE = ["super_admin", "hr", "manager"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }

const TYPES = ["Internal", "External", "Webinar", "Workshop", "Self-paced"];
const AUDIENCES = ["All employees", "Medical Coding", "Home Health Medical Coding", "Quality", "Leadership", "New joiners"];

export default function Training() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);

  const [trainings, setTrainings] = useState([]);
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [refresh, setRefresh] = useState(0);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    trainer: "",
    type: "Internal",
    audience: "All employees",
    date: "",
    time: "10:00",
    duration_hours: 2,
    location: "",
    description: "",
    status: "scheduled",
  });

  useEffect(() => {
    api.get("/trainings").then((r) => setTrainings(r.data));
  }, [refresh]);

  const reset = () => setForm({
    title: "", trainer: "", type: "Internal", audience: "All employees",
    date: "", time: "10:00", duration_hours: 2, location: "", description: "", status: "scheduled",
  });

  const submit = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    if (!form.date) { toast.error("Date is required"); return; }
    setSubmitting(true);
    try {
      await api.post("/trainings", { ...form, duration_hours: Number(form.duration_hours) || 1 });
      toast.success("Training scheduled");
      setOpen(false);
      reset();
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const calendar = useMemo(() => {
    const first = startOfMonth(month);
    const total = daysInMonth(month);
    const startDay = first.getDay();
    const map = {};
    trainings.forEach((t) => {
      if (!map[t.date]) map[t.date] = [];
      map[t.date].push(t);
    });
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= total; d++) {
      const date = new Date(month.getFullYear(), month.getMonth(), d);
      const iso = date.toISOString().slice(0, 10);
      cells.push({ date, iso, items: map[iso] || [] });
    }
    return cells;
  }, [trainings, month]);

  const monthLabel = month.toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <div data-testid="training-page">
      <PageHeader
        title="Training & Learning"
        subtitle="Calendar of upcoming sessions, certifications, and assessments."
        action={
          canManage ? (
            <Button data-testid="schedule-training" onClick={() => setOpen(true)} className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]">
              <Plus className="h-4 w-4 mr-1.5" /> Schedule training
            </Button>
          ) : null
        }
      />

      <Tabs defaultValue="calendar">
        <TabsList className="bg-transparent border-b border-gray-200 rounded-none w-full justify-start h-auto p-0 gap-6 mb-6">
          <TabsTrigger value="calendar" data-testid="tab-calendar" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">Calendar</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">All sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          <Card className="hr-card p-5" data-testid="training-calendar">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg font-semibold text-[#0F172A]">{monthLabel}</div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="train-cal-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="train-cal-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-2 mb-2">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="text-[10px] uppercase tracking-wider text-gray-400 text-center">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {calendar.map((cell, i) => {
                if (!cell) return <div key={`blank-${i}`} />;
                const isToday = cell.iso === new Date().toISOString().slice(0, 10);
                const has = cell.items.length > 0;
                return (
                  <div key={cell.iso} className={`border rounded-md p-2 min-h-[80px] text-xs ${has ? "bg-[#1E88E5]/10 border-[#1E88E5]/30" : "bg-white border-gray-200"} ${isToday ? "ring-2 ring-[#F26B3A]" : ""}`}>
                    <div className="font-medium">{cell.date.getDate()}</div>
                    {cell.items.slice(0, 2).map((t) => (
                      <div key={t.id} className="text-[10px] mt-1 truncate text-[#0F172A]" title={t.title}>
                        <GraduationCap className="h-2.5 w-2.5 inline mr-0.5" />{t.title}
                      </div>
                    ))}
                    {cell.items.length > 2 && <div className="text-[10px] text-gray-500 mt-0.5">+{cell.items.length - 2} more</div>}
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="list">
          <DataTable
            testId="trainings-table"
            endpoint="/trainings"
            refreshKey={refresh}
            columns={[
              { key: "title", label: "Title", render: (r) => (
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-[#0F172A]" />
                  <span className="font-medium">{r.title}</span>
                </div>
              )},
              { key: "trainer", label: "Trainer" },
              { key: "audience", label: "Audience" },
              { key: "date", label: "Date" },
              { key: "duration_hours", label: "Duration", render: (r) => `${r.duration_hours} hr` },
              { key: "status", label: "Status", badge: true },
            ]}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-lg" data-testid="schedule-training-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-[#F26B3A]" /> Schedule training</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Title</Label>
              <Input data-testid="train-title" className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. ICD-10-CM Refresher" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Trainer</Label>
              <Input data-testid="train-trainer" className="mt-1.5" value={form.trainer} onChange={(e) => setForm({ ...form, trainer: e.target.value })} placeholder="Internal SME / External name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="train-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Audience</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger data-testid="train-audience-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AUDIENCES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Date</Label>
              <Input data-testid="train-date" type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Time</Label>
              <Input data-testid="train-time" type="time" className="mt-1.5" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Duration (hours)</Label>
              <Input data-testid="train-duration" type="number" min="0.5" step="0.5" className="mt-1.5" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Location / Link</Label>
              <Input data-testid="train-location" className="mt-1.5" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Conference Room 2 · or Google Meet link" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label>
              <Textarea rows={2} data-testid="train-desc" className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Agenda, prerequisites, expected outcomes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="train-cancel" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="train-submit" onClick={submit} disabled={submitting} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
